import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HistoryIcon from '@mui/icons-material/History';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DokumentenAblage from '../components/common/DokumentenAblage';
import { format } from 'date-fns';
import AppBreadcrumbs from '../components/common/AppBreadcrumbs';
import DiskussionContextLink from '../components/diskussion/DiskussionContextLink';
import FormSkeleton from '../components/common/FormSkeleton';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { MitarbeiterKurzprofilLink } from '../components/common/MitarbeiterKurzprofilLink';
import HistorieTimeline from '../components/common/HistorieTimeline';
import AuditProgressPipeline from '../components/common/AuditProgressPipeline';
import { isProjectSuccessTerminalStatus, splitWorkflowStatuses } from '../utils/workflowStatus';
import { projekteService } from '../services/projekteService';
import { statusService, auditStatusService } from '../services/lookupService';
import { mitarbeiterService } from '../services/mitarbeiterService';
import { kundenService } from '../services/kundenService';
import { auditsService } from '../services/auditsService';
import zeiteintraegeService from '../services/zeiteintraegeService';
import rechnungenService from '../services/rechnungenService';
import angeboteService from '../services/angeboteService';
import * as P from '../constants/permissions';
import {
  Projekt, ProjektCreate, Status, MitarbeiterList, Kunde, AuditList,
  Zeiteintrag, ZeiteintragCreate, RechnungList, AuditStatus, FileEntity, AngebotList,
} from '../types';
import { useNotification } from '../hooks/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';
import { toLocalDateString } from '../utils/time';
import {
  isFestpreisProjektTyp,
  isKontingentProjektTyp,
  normalizeProjektTyp,
  projektTypAccent,
  projektTypLabel,
} from '../utils/projektTyp';

/* ── Accordion styling (matching AuditDetailPage) ─────────────────────── */
const accordionSx = (isDark: boolean) => ({
  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
  borderRadius: '12px !important',
  overflow: 'hidden',
  mb: 2,
  '&:before': { display: 'none' },
  '&.Mui-expanded': { mb: 2 },
  boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.04)',
});

const accordionSummarySx = (isDark: boolean) => ({
  background: isDark
    ? 'linear-gradient(135deg, rgba(30,36,68,0.7) 0%, rgba(22,28,52,0.5) 100%)'
    : 'linear-gradient(135deg, rgba(245,247,252,1) 0%, rgba(238,241,248,1) 100%)',
  minHeight: 56,
  '&.Mui-expanded': { minHeight: 56 },
  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 },
  '& .MuiAccordionSummary-content.Mui-expanded': { margin: '12px 0' },
});

/* ── Infobox field label ──────────────────────────────────────────────── */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      fontSize: '0.5rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: 'text.disabled',
      display: 'block',
      mb: 0.25,
    }}
  >
    {children}
  </Typography>
);

/* ── Helpers ──────────────────────────────────────────────────────────── */
const parseOptionalNumber = (raw: string): number | undefined => {
  const v = raw.trim().replace(',', '.');
  if (!v) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const formatPt = (n?: number | null): string => {
  if (n == null) return '-';
  return n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
};

const formatEur = (n?: number | null): string => {
  if (n == null) return '-';
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

type ProjektPtFields = Pick<
  ProjektCreate,
  | 'personentage'
  | 'personentage_onsite'
  | 'personentage_remote'
  | 'tagessatz'
  | 'tagessatz_onsite'
  | 'tagessatz_remote'
>;

const hasPtSplit = (d: ProjektPtFields): boolean =>
  d.personentage_onsite != null
  || d.personentage_remote != null
  || d.tagessatz_onsite != null
  || d.tagessatz_remote != null;

const computeGesamtwert = (d: ProjektPtFields): number => {
  if (hasPtSplit(d)) {
    return (d.personentage_onsite ?? 0) * (d.tagessatz_onsite ?? 0)
      + (d.personentage_remote ?? 0) * (d.tagessatz_remote ?? 0);
  }
  return (d.personentage ?? 0) * (d.tagessatz ?? 0);
};

const totalPersonentage = (d: ProjektPtFields): number | undefined => {
  if (hasPtSplit(d)) {
    const total = (d.personentage_onsite ?? 0) + (d.personentage_remote ?? 0);
    return total > 0 ? total : undefined;
  }
  return d.personentage;
};

const projektToFormData = (p: Projekt): ProjektCreate => ({
  name: p.name,
  beschreibung: p.beschreibung || '',
  personentage: p.personentage,
  personentage_onsite: p.personentage_onsite,
  personentage_remote: p.personentage_remote,
  projekt_typ: normalizeProjektTyp(p.projekt_typ),
  tagessatz: p.tagessatz,
  tagessatz_onsite: p.tagessatz_onsite,
  tagessatz_remote: p.tagessatz_remote,
  stunden_pro_pt: p.stunden_pro_pt || 8.0,
  status_id: p.status_id,
  projektleiter_id: p.projektleiter_id,
  kunde_id: p.kunde_id,
  mitarbeiter_ids: p.mitarbeiter_team.map((m) => m.id),
});

const getProjectStatusColor = (statusName?: string): string => {
  if (!statusName) return '#757575';
  const lower = statusName.toLowerCase();
  if (lower.includes('abgeschlossen') || lower.includes('beendet') || lower.includes('abgerechnet') || lower.includes('fertig') || lower.includes('done')) return '#4caf50';
  if (lower.includes('bearbeitung') || lower.includes('aktiv') || lower.includes('progress')) return '#2196f3';
  if (lower.includes('geplant') || lower.includes('planned') || lower.includes('neu')) return '#ff9800';
  if (lower.includes('abgebrochen') || lower.includes('storniert') || lower.includes('cancel')) return '#f44336';
  if (lower.includes('pause') || lower.includes('hold')) return '#9e9e9e';
  return '#42a5f5';
};

const getInvoiceStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const m: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    Entwurf: 'default', Versendet: 'primary', Bezahlt: 'success', Storniert: 'error',
  };
  return m[status] || 'default';
};

type AbgerechnetFilter = 'alle' | 'abgerechnet' | 'nicht_abgerechnet';

/* ══════════════════════════════════════════════════════════════════════ */

const ProjektDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isNew = id === 'neu';
  const { showSuccess } = useNotification();
  const { hasAnyRole, hasPermission } = usePermissions();
  const canEditZeiteintraege = hasAnyRole('Management', 'Admin');
  const canUnlinkAbrechnung = hasPermission(P.RECHNUNGEN_EDIT);
  const canViewAngebote = hasPermission(P.ANGEBOTE_VIEW);

  /* ── Core state ─────────────────────────────────────────────────────── */
  const [projekt, setProjekt] = useState<Projekt | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState<FileEntity[]>([]);
  const [loadingFilesToDelete, setLoadingFilesToDelete] = useState(false);
  const [historieOpen, setHistorieOpen] = useState(false);

  /* ── Accordion state ────────────────────────────────────────────────── */
  const [expandedPanels, setExpandedPanels] = useState({
    audits: true,
    rechnungen: true,
    dokumente: true,
    zeiterfassung: false,
    beschreibung: false,
  });
  const [dokumentCount, setDokumentCount] = useState(0);

  /* ── Inline-edit menu anchors ───────────────────────────────────────── */
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [kundeMenuAnchor, setKundeMenuAnchor] = useState<null | HTMLElement>(null);
  const [plMenuAnchor, setPlMenuAnchor] = useState<null | HTMLElement>(null);
  const [teamMenuAnchor, setTeamMenuAnchor] = useState<null | HTMLElement>(null);
  const [typMenuAnchor, setTypMenuAnchor] = useState<null | HTMLElement>(null);

  /* ── Form state ─────────────────────────────────────────────────────── */
  const [formData, setFormData] = useState<ProjektCreate>({
    name: '',
    beschreibung: '',
    personentage: undefined,
    personentage_onsite: undefined,
    personentage_remote: undefined,
    projekt_typ: 'Single',
    tagessatz: undefined,
    tagessatz_onsite: undefined,
    tagessatz_remote: undefined,
    stunden_pro_pt: 8.0,
    status_id: undefined,
    projektleiter_id: undefined,
    kunde_id: undefined,
    mitarbeiter_ids: [],
  });

  /* ── Lookup data ────────────────────────────────────────────────────── */
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [auditStatuses, setAuditStatuses] = useState<AuditStatus[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState<MitarbeiterList[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [audits, setAudits] = useState<AuditList[]>([]);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [loadingAudits, setLoadingAudits] = useState(false);

  /* ── Zeiterfassung state ────────────────────────────────────────────── */
  const [zeiteintraege, setZeiteintraege] = useState<Zeiteintrag[]>([]);
  const [loadingZeiteintraege, setLoadingZeiteintraege] = useState(false);
  const [zeiteintragDialogOpen, setZeiteintragDialogOpen] = useState(false);
  const [editingZeiteintragId, setEditingZeiteintragId] = useState<number | null>(null);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<Zeiteintrag | null>(null);
  const [zeitnachweisDialogMonat, setZeitnachweisDialogMonat] = useState<string | null>(null);
  const [zeiteintragFilterVon, setZeiteintragFilterVon] = useState('');
  const [zeiteintragFilterBis, setZeiteintragFilterBis] = useState('');
  const [zeiteintragFilterMitarbeiterId, setZeiteintragFilterMitarbeiterId] = useState<number | ''>('');
  const [zeiteintragFilterAuditId, setZeiteintragFilterAuditId] = useState<number | ''>('');
  const [zeiteintragFilterAbgerechnet, setZeiteintragFilterAbgerechnet] = useState<AbgerechnetFilter>('alle');
  const [zeiteintragForm, setZeiteintragForm] = useState<ZeiteintragCreate>({
    datum: toLocalDateString(),
    stunden: 8,
    beschreibung: '',
    mitarbeiter_id: 0,
    projekt_id: 0,
    audit_id: undefined,
  });
  /** Nur bei Neuanlage: mehrere Mitarbeiter → mehrere POSTs mit gleichen übrigen Feldern */
  const [neuerZeiteintragMitarbeiterIds, setNeuerZeiteintragMitarbeiterIds] = useState<number[]>([]);

  /* ── Rechnungen state ───────────────────────────────────────────────── */
  const [rechnungen, setRechnungen] = useState<RechnungList[]>([]);
  const [loadingRechnungen, setLoadingRechnungen] = useState(false);
  const [angebote, setAngebote] = useState<AngebotList[]>([]);

  /* ── Schnellabrechnung state ────────────────────────────────────────── */
  const [offeneMonate, setOffeneMonate] = useState<{ monat: string; stunden: number; anzahl: number }[]>([]);
  const [schnellabrechnungMonat, setSchnellabrechnungMonat] = useState<{ monat: string; stunden: number; anzahl: number } | null>(null);
  const [schnellabrechnungLoading, setSchnellabrechnungLoading] = useState(false);

  /* ── Data loading ───────────────────────────────────────────────────── */
  useEffect(() => {
    const loadLookups = async () => {
      try {
        setLoadingLookups(true);
        const [statusesData, mitarbeiterData, kundenData, auditStatusesData] = await Promise.all([
          statusService.list(),
          mitarbeiterService.list(),
          kundenService.list(),
          auditStatusService.list(),
        ]);
        setStatuses(statusesData);
        setMitarbeiter(mitarbeiterData);
        setKunden(kundenData);
        setAuditStatuses(auditStatusesData);
      } catch (error) {
        console.error('Fehler beim Laden der Lookup-Daten:', error);
      } finally {
        setLoadingLookups(false);
      }
    };
    loadLookups();
  }, []);

  const loadProjekt = useCallback(async () => {
    if (isNew || !id) return;
    try {
      setLoading(true);
      const p = await projekteService.get(parseInt(id));
      setProjekt(p);
      setFormData(projektToFormData(p));
    } catch (error) {
      console.error('Fehler beim Laden des Projekts:', error);
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { loadProjekt(); }, [loadProjekt]);

  const loadAudits = useCallback(async () => {
    if (isNew || !id) return;
    try {
      setLoadingAudits(true);
      setAudits(await auditsService.list({ projekt_id: parseInt(id) }));
    } catch (error) {
      console.error('Fehler beim Laden der Audits:', error);
    } finally {
      setLoadingAudits(false);
    }
  }, [id, isNew]);

  const loadZeiteintraege = useCallback(async () => {
    if (!id || isNew) return;
    try {
      setLoadingZeiteintraege(true);
      const params: {
        projekt_id: number;
        datum_von?: string;
        datum_bis?: string;
        mitarbeiter_id?: number;
        audit_id?: number;
        abgerechnet?: boolean;
      } = { projekt_id: parseInt(id) };
      if (zeiteintragFilterVon) params.datum_von = zeiteintragFilterVon;
      if (zeiteintragFilterBis) params.datum_bis = zeiteintragFilterBis;
      if (zeiteintragFilterMitarbeiterId !== '') params.mitarbeiter_id = zeiteintragFilterMitarbeiterId;
      if (zeiteintragFilterAuditId !== '') params.audit_id = zeiteintragFilterAuditId;
      if (zeiteintragFilterAbgerechnet === 'abgerechnet') params.abgerechnet = true;
      if (zeiteintragFilterAbgerechnet === 'nicht_abgerechnet') params.abgerechnet = false;
      setZeiteintraege(await zeiteintraegeService.list(params));
    } catch (error) {
      console.error('Fehler beim Laden der Zeiteintraege:', error);
    } finally {
      setLoadingZeiteintraege(false);
    }
  }, [
    id,
    isNew,
    zeiteintragFilterVon,
    zeiteintragFilterBis,
    zeiteintragFilterMitarbeiterId,
    zeiteintragFilterAuditId,
    zeiteintragFilterAbgerechnet,
  ]);

  const loadRechnungen = useCallback(async () => {
    if (!id || isNew) return;
    try {
      setLoadingRechnungen(true);
      setRechnungen(await rechnungenService.list({ projekt_id: parseInt(id) }));
    } catch (error) {
      console.error('Fehler beim Laden der Rechnungen:', error);
    } finally {
      setLoadingRechnungen(false);
    }
  }, [id, isNew]);

  const loadAngebote = useCallback(async () => {
    if (!id || isNew || !canViewAngebote) return;
    try {
      setAngebote(await angeboteService.list({ projekt_id: parseInt(id) }));
    } catch {
      // Interceptor handles error display
    }
  }, [id, isNew, canViewAngebote]);

  const loadOffeneMonate = useCallback(async () => {
    if (!id || isNew) return;
    try {
      setOffeneMonate(await rechnungenService.getOffeneMonate(parseInt(id)));
    } catch (error) {
      console.error('Fehler beim Laden der offenen Monate:', error);
    }
  }, [id, isNew]);

  useEffect(() => {
    if (!isNew && id) {
      loadAudits();
      loadRechnungen();
      loadAngebote();
      loadZeiteintraege();
      loadOffeneMonate();
    }
  }, [isNew, id, loadAudits, loadRechnungen, loadAngebote, loadZeiteintraege, loadOffeneMonate]);

  /* ── Handlers ───────────────────────────────────────────────────────── */
  const handleAccordionChange = (panel: keyof typeof expandedPanels) =>
    (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels((prev) => ({ ...prev, [panel]: isExpanded }));
    };

  const handleInputChange = (field: keyof ProjektCreate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStartEditing = () => setEditing(true);

  const handleCancelEditing = () => {
    if (projekt) {
      setFormData(projektToFormData(projekt));
    }
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (isNew) {
        await projekteService.create(formData);
        showSuccess('Projekt erfolgreich erstellt');
        setTimeout(() => navigate('/projekte'), 1000);
      } else if (id) {
        const updated = await projekteService.update(parseInt(id), formData);
        setProjekt(updated);
        showSuccess('Projekt erfolgreich aktualisiert');
        setEditing(false);
      }
    } catch { /* handled by global interceptor */ } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    try {
      await projekteService.delete(parseInt(id));
      showSuccess('Projekt erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      setTimeout(() => navigate('/projekte'), 1000);
    } catch {
      setDeleteDialogOpen(false);
    }
  };

  const handleSchnellabrechnung = async () => {
    if (!id || !schnellabrechnungMonat) return;
    try {
      setSchnellabrechnungLoading(true);
      const rechnung = await rechnungenService.schnellabrechnung(parseInt(id), schnellabrechnungMonat.monat);
      showSuccess(`Rechnung ${rechnung.rechnungsnummer} erstellt`);
      setSchnellabrechnungMonat(null);
      loadRechnungen();
      loadOffeneMonate();
      loadProjekt();
    } catch {
      setSchnellabrechnungMonat(null);
    } finally {
      setSchnellabrechnungLoading(false);
    }
  };

  const handleSaveZeiteintrag = async () => {
    if (!id) return;
    try {
      if (editingZeiteintragId) {
        await zeiteintraegeService.update(editingZeiteintragId, { ...zeiteintragForm, projekt_id: parseInt(id) });
        showSuccess('Zeiteintrag aktualisiert');
      } else {
        if (neuerZeiteintragMitarbeiterIds.length === 0) return;
        const pid = parseInt(id, 10);
        for (const mid of neuerZeiteintragMitarbeiterIds) {
          await zeiteintraegeService.create({ ...zeiteintragForm, mitarbeiter_id: mid, projekt_id: pid });
        }
        const n = neuerZeiteintragMitarbeiterIds.length;
        showSuccess(n === 1 ? 'Zeiteintrag erstellt' : `${n} Zeiteinträge erstellt`);
      }
      setZeiteintragDialogOpen(false);
      setEditingZeiteintragId(null);
      loadZeiteintraege();
      loadProjekt();
    } catch { /* handled */ }
  };

  const openEditZeiteintragDialog = (ze: Zeiteintrag) => {
    setEditingZeiteintragId(ze.id);
    setZeiteintragForm({
      datum: ze.datum.split('T')[0],
      stunden: ze.stunden,
      beschreibung: ze.beschreibung || '',
      mitarbeiter_id: ze.mitarbeiter_id,
      projekt_id: ze.projekt_id,
      audit_id: ze.audit_id,
    });
    setZeiteintragDialogOpen(true);
  };

  const handleDeleteZeiteintrag = async (zeId: number) => {
    try {
      await zeiteintraegeService.delete(zeId);
      showSuccess('Zeiteintrag gelöscht');
      loadZeiteintraege();
      loadProjekt();
    } catch { /* handled */ }
  };

  const handleUnlinkAbrechnung = async () => {
    if (!unlinkTarget) return;
    try {
      await zeiteintraegeService.loeseAbrechnung(unlinkTarget.id);
      showSuccess('Abrechnungsmarkierung aufgehoben');
      setUnlinkConfirmOpen(false);
      setUnlinkTarget(null);
      loadZeiteintraege();
      loadOffeneMonate();
    } catch { /* handled */ }
  };

  const handleResetZeiteintragFilter = () => {
    setZeiteintragFilterVon('');
    setZeiteintragFilterBis('');
    setZeiteintragFilterMitarbeiterId('');
    setZeiteintragFilterAuditId('');
    setZeiteintragFilterAbgerechnet('alle');
  };

  const getMonths = (): string[] => {
    const months = new Set<string>();
    zeiteintraege.forEach((ze) => {
      const d = new Date(ze.datum);
      months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    });
    return Array.from(months).sort().reverse();
  };

  /* ── Lookup helpers ─────────────────────────────────────────────────── */
  const getStatusName = (sid?: number) => statuses.find((s) => s.id === sid)?.name;
  const getProjektleiterName = (pid?: number) => {
    const m = mitarbeiter.find((ma) => ma.id === pid);
    return m ? `${m.vorname} ${m.nachname}` : undefined;
  };
  const getAuditName = (auditId?: number) => {
    if (!auditId) return '–';
    const a = audits.find((x) => x.id === auditId);
    return a?.name || `Audit #${auditId}`;
  };
  const getKundeName = (kid?: number) => kunden.find((k) => k.id === kid)?.name_des_unternehmens;

  /* ── Loading ────────────────────────────────────────────────────────── */
  if (loading || loadingLookups) {
    return <FormSkeleton fields={6} hasTabs={!isNew} />;
  }

  /* ── Computed values ────────────────────────────────────────────────── */
  const isKontingent = isKontingentProjektTyp(projekt?.projekt_typ || formData.projekt_typ);
  const isFestpreis = isFestpreisProjektTyp(projekt?.projekt_typ || formData.projekt_typ);
  const kontingentProgress = projekt && projekt.personentage
    ? Math.min((projekt.verbrauchte_pt / projekt.personentage) * 100, 100)
    : 0;

  const gesamtwert = computeGesamtwert(formData);
  const gesamtPt = totalPersonentage(formData);
  const rechnungenSumNetto = rechnungen.reduce((s, r) => s + r.nettobetrag, 0);
  const bezahltSum = rechnungen.filter((r) => r.status === 'Bezahlt').reduce((s, r) => s + r.nettobetrag, 0);
  const offenerBetrag = gesamtwert > 0 ? gesamtwert - bezahltSum : 0;

  const statusName = getStatusName(formData.status_id);
  const statusColor = getProjectStatusColor(statusName);
  const { flowStatuses, cancelledStatuses } = splitWorkflowStatuses(statuses);
  const activeFlowStatuses = flowStatuses.filter((s) => !isProjectSuccessTerminalStatus(s.name));
  const successTerminalStatuses = flowStatuses.filter((s) => isProjectSuccessTerminalStatus(s.name));

  /* ══════════════════════════════════════════════════════════════════════
     NEW PROJECT FORM
     ══════════════════════════════════════════════════════════════════════ */
  if (isNew) {
    return (
      <Box>
        <AppBreadcrumbs items={[{ label: 'Projekte', path: '/projekte' }, { label: 'Neues Projekt' }]} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, mt: 1 }}>Neues Projekt</Typography>
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField fullWidth label="Name" value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)} required />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Beschreibung" value={formData.beschreibung}
                  onChange={(e) => handleInputChange('beschreibung', e.target.value)} multiline rows={4} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Projekttyp</InputLabel>
                  <Select value={formData.projekt_typ || 'Single'}
                    onChange={(e) => handleInputChange('projekt_typ', e.target.value)} label="Projekttyp">
                    <MenuItem value="Single">Single (Einzelprojekt)</MenuItem>
                    <MenuItem value="Kontingent">Kontingent (Laufend)</MenuItem>
                    <MenuItem value="Festpreis">Festpreis</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {!isFestpreis && (
                <>
              {!hasPtSplit(formData) && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth
                      label={isKontingentProjektTyp(formData.projekt_typ) ? 'Kontingent (PT)' : 'Personentage'}
                      type="number" value={formData.personentage ?? ''}
                      onChange={(e) => handleInputChange('personentage', parseOptionalNumber(e.target.value))}
                      helperText="Auch halbe Tage, z. B. 6,5"
                      InputProps={{ inputProps: { step: 0.5, min: 0 } }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Tagessatz (EUR)" type="number"
                      value={formData.tagessatz ?? ''}
                      onChange={(e) => handleInputChange('tagessatz', parseOptionalNumber(e.target.value))}
                      InputProps={{ inputProps: { step: 0.01, min: 0 } }} />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  On-Site / Remote (optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Wenn die Tagessätze abweichen, Personentage hier getrennt erfassen. Der Gesamtwert ergibt sich aus beiden Zeilen.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="On-Site (PT)" type="number"
                      value={formData.personentage_onsite ?? ''}
                      onChange={(e) => handleInputChange('personentage_onsite', parseOptionalNumber(e.target.value))}
                      InputProps={{ inputProps: { step: 0.5, min: 0 } }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Tagessatz On-Site (EUR)" type="number"
                      value={formData.tagessatz_onsite ?? ''}
                      onChange={(e) => handleInputChange('tagessatz_onsite', parseOptionalNumber(e.target.value))}
                      InputProps={{ inputProps: { step: 0.01, min: 0 } }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Remote (PT)" type="number"
                      value={formData.personentage_remote ?? ''}
                      onChange={(e) => handleInputChange('personentage_remote', parseOptionalNumber(e.target.value))}
                      InputProps={{ inputProps: { step: 0.5, min: 0 } }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Tagessatz Remote (EUR)" type="number"
                      value={formData.tagessatz_remote ?? ''}
                      onChange={(e) => handleInputChange('tagessatz_remote', parseOptionalNumber(e.target.value))}
                      InputProps={{ inputProps: { step: 0.01, min: 0 } }} />
                  </Grid>
                </Grid>
                {hasPtSplit(formData) && (gesamtPt != null || gesamtwert > 0) && (
                  <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                    Gesamt: {formatPt(gesamtPt)} PT
                    {gesamtwert > 0 ? ` · ${formatEur(gesamtwert)}` : ''}
                  </Typography>
                )}
              </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={formData.status_id || ''}
                    onChange={(e) => handleInputChange('status_id', e.target.value || undefined)} label="Status">
                    <MenuItem value=""><em>Keine Auswahl</em></MenuItem>
                    {activeFlowStatuses.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                    {(successTerminalStatuses.length > 0 || cancelledStatuses.length > 0) && (
                      <MenuItem disabled sx={{ fontSize: '0.75rem', opacity: 0.7 }}>Abschluss (direkt setzen)</MenuItem>
                    )}
                    {successTerminalStatuses.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                    {cancelledStatuses.map((s) => <MenuItem key={s.id} value={s.id} sx={{ color: 'error.main' }}>{s.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Projektleiter</InputLabel>
                  <Select value={formData.projektleiter_id || ''}
                    onChange={(e) => handleInputChange('projektleiter_id', e.target.value || undefined)} label="Projektleiter">
                    <MenuItem value=""><em>Keine Auswahl</em></MenuItem>
                    {mitarbeiter.map((m) => <MenuItem key={m.id} value={m.id}>{m.vorname} {m.nachname}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Kunde</InputLabel>
                  <Select value={formData.kunde_id || ''}
                    onChange={(e) => handleInputChange('kunde_id', e.target.value || undefined)} label="Kunde">
                    <MenuItem value=""><em>Keine Auswahl</em></MenuItem>
                    {kunden.map((k) => <MenuItem key={k.id} value={k.id}>{k.name_des_unternehmens}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Mitarbeiter (Team)</InputLabel>
                  <Select multiple value={formData.mitarbeiter_ids || []}
                    onChange={(e) => handleInputChange('mitarbeiter_ids', e.target.value as number[])}
                    label="Mitarbeiter (Team)"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((v) => {
                          const m = mitarbeiter.find((ma) => ma.id === v);
                          return <Chip key={v} label={m ? `${m.vorname} ${m.nachname}` : v} size="small" />;
                        })}
                      </Box>
                    )}>
                    {mitarbeiter.map((m) => <MenuItem key={m.id} value={m.id}>{m.vorname} {m.nachname}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="contained" onClick={handleSave} disabled={saving || !formData.name}>
                    {saving ? <CircularProgress size={24} /> : 'Speichern'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  /* ══════════════════════════════════════════════════════════════════════
     EXISTING PROJECT VIEW
     ══════════════════════════════════════════════════════════════════════ */
  return (
    <Box>
      {/* ── Breadcrumbs + Action buttons ─────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <AppBreadcrumbs items={[{ label: 'Projekte', path: '/projekte' }, { label: projekt?.name || 'Projekt' }]} />
        {!editing && (
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 2 }}>
            <Tooltip title="Änderungshistorie">
              <IconButton size="small" onClick={() => setHistorieOpen(true)}
                sx={{ color: 'text.secondary', '&:hover': { color: '#42a5f5', bgcolor: 'rgba(66,165,245,0.1)' } }}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Löschen">
              <IconButton
                size="small"
                onClick={async () => {
                  if (!id) return;
                  setLoadingFilesToDelete(true);
                  try {
                    const files = await projekteService.getFilesToDelete(parseInt(id));
                    setFilesToDelete(files);
                    setDeleteDialogOpen(true);
                  } catch {
                    setFilesToDelete([]);
                    setDeleteDialogOpen(true);
                  } finally {
                    setLoadingFilesToDelete(false);
                  }
                }}
                disabled={loadingFilesToDelete}
                sx={{ color: 'text.secondary', '&:hover': { color: '#ef5350', bgcolor: 'rgba(239,83,80,0.1)' } }}
              >
                {loadingFilesToDelete ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {id && !editing && (
        <DiskussionContextLink
          anchor={{ refType: 'projekt', refId: parseInt(id, 10) }}
          objectLabel={projekt?.name}
        />
      )}

      {angebote.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<DescriptionOutlinedIcon fontSize="inherit" />}>
          Quell-Angebot{angebote.length > 1 ? 'e' : ''}:{' '}
          {angebote.map((a, idx) => (
            <React.Fragment key={a.id}>
              {idx > 0 && ', '}
              <Box
                component="span"
                sx={{ fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate(`/angebote/${a.id}`)}
              >
                {a.angebotsnummer}{a.titel ? ` – ${a.titel}` : ''}
              </Box>
            </React.Fragment>
          ))}
        </Alert>
      )}

      {/* ── Infobox (left) + Status / KPI area (right) ───────────────── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'stretch' }}>

        {/* ── Left: Compact Infobox ─────────────────────────────────── */}
        <Box
          sx={{
            flex: '0 0 calc(100% / 6 * 1.3)',
            minWidth: 182,
            maxWidth: 286,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            px: 1.5,
            py: 1.5,
            borderRadius: 2,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            position: 'relative',
          }}
        >
          {/* Edit / Save+Cancel */}
          {editing ? (
            <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.25 }}>
              <Tooltip title="Speichern">
                <IconButton size="small" onClick={handleSave} disabled={saving || !formData.name}
                  sx={{ width: 22, height: 22, color: '#3dd47e', '&:hover': { bgcolor: 'rgba(61,212,126,0.15)' } }}>
                  {saving ? <CircularProgress size={12} color="inherit" /> : <CheckIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Abbrechen">
                <IconButton size="small" onClick={handleCancelEditing}
                  sx={{ width: 22, height: 22, color: 'text.disabled', '&:hover': { color: '#ef5350', bgcolor: 'rgba(239,83,80,0.1)' } }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Tooltip title="Bearbeiten">
              <IconButton size="small" onClick={handleStartEditing}
                sx={{ position: 'absolute', top: 6, right: 6, color: 'text.disabled', '&:hover': { color: '#3dd47e', bgcolor: 'rgba(61,212,126,0.1)' } }}>
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Name (edit mode only) */}
          {editing && (
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel>Name</FieldLabel>
              <TextField variant="standard" size="small" value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                InputProps={{ disableUnderline: true }}
                sx={{ '& input': { fontSize: '0.72rem', fontWeight: 500, p: 0, lineHeight: 1.2 } }} />
            </Box>
          )}

          {/* Projekttyp */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Typ</FieldLabel>
            <Box
              component="span"
              {...(editing && { onClick: (e: React.MouseEvent<HTMLElement>) => setTypMenuAnchor(e.currentTarget) })}
              sx={{
                display: 'inline-flex', alignItems: 'center', px: 0.6, borderRadius: 0.75,
                fontSize: '0.6rem', fontWeight: 700, color: '#fff',
                backgroundColor: projektTypAccent(formData.projekt_typ),
                height: 18, cursor: editing ? 'pointer' : 'default',
              }}
            >
              {projektTypLabel(formData.projekt_typ)}
            </Box>
          </Box>

          {/* Status (in infobox for quick visibility) */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Status</FieldLabel>
            <Box
              component="span"
              {...(editing && { onClick: (e: React.MouseEvent<HTMLElement>) => setStatusMenuAnchor(e.currentTarget) })}
              sx={{
                display: 'inline-flex', alignItems: 'center', px: 0.6, borderRadius: 0.75,
                fontSize: '0.6rem', fontWeight: 700, color: '#fff',
                backgroundColor: statusColor, height: 18,
                cursor: editing ? 'pointer' : 'default',
              }}
            >
              {statusName || 'Kein Status'}
            </Box>
          </Box>

          {/* Kunde */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Kunde</FieldLabel>
            {editing ? (
              <Typography onClick={(e) => setKundeMenuAnchor(e.currentTarget)}
                sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2, cursor: 'pointer', '&:hover': { color: 'primary.main' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getKundeName(formData.kunde_id) || 'Auswählen…'}
              </Typography>
            ) : (
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getKundeName(formData.kunde_id) || '-'}
              </Typography>
            )}
          </Box>

          {/* Projektleiter */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Projektleiter</FieldLabel>
            {editing ? (
              <Typography onClick={(e) => setPlMenuAnchor(e.currentTarget)}
                sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2, cursor: 'pointer', '&:hover': { color: 'primary.main' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getProjektleiterName(formData.projektleiter_id) || 'Auswählen…'}
              </Typography>
            ) : (
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getProjektleiterName(formData.projektleiter_id) || '-'}
              </Typography>
            )}
          </Box>

          {/* Team */}
          {((formData.mitarbeiter_ids?.length ?? 0) > 0 || editing) && (
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel>Team</FieldLabel>
              <Box sx={{ display: 'flex', gap: 0.35, flexWrap: 'wrap', alignItems: 'center' }}>
                {(formData.mitarbeiter_ids || []).map((mid) => {
                  const m = mitarbeiter.find((ma) => ma.id === mid);
                  const label = m ? `${m.vorname} ${m.nachname}` : String(mid);
                  return (
                    <Chip key={mid} label={label} size="small" variant="outlined"
                      {...(editing && {
                        onDelete: () => handleInputChange('mitarbeiter_ids', (formData.mitarbeiter_ids || []).filter((x) => x !== mid)),
                        deleteIcon: <CloseIcon sx={{ fontSize: '12px !important' }} />,
                      })}
                      sx={{ height: 18, fontSize: '0.62rem', borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' }} />
                  );
                })}
                {editing && (
                  <IconButton size="small" onClick={(e) => setTeamMenuAnchor(e.currentTarget)}
                    sx={{ width: 18, height: 18, bgcolor: 'rgba(61,212,126,0.1)' }}>
                    <AddIcon sx={{ fontSize: 12, color: '#3dd47e' }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          {/* Personentage / Tagessätze */}
          {!isFestpreis && (editing ? (
            <>
              {!hasPtSplit(formData) && (
                <>
                  <Box sx={{ minWidth: 0 }}>
                    <FieldLabel>{isKontingent ? 'Kontingent (PT)' : 'Personentage'}</FieldLabel>
                    <TextField variant="standard" size="small" type="number"
                      value={formData.personentage ?? ''}
                      onChange={(e) => handleInputChange('personentage', parseOptionalNumber(e.target.value))}
                      InputProps={{ disableUnderline: true, inputProps: { step: 0.5, min: 0 } }}
                      sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 } }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <FieldLabel>Tagessatz</FieldLabel>
                    <TextField variant="standard" size="small" type="number"
                      value={formData.tagessatz ?? ''}
                      onChange={(e) => handleInputChange('tagessatz', parseOptionalNumber(e.target.value))}
                      InputProps={{ disableUnderline: true, inputProps: { step: 0.01, min: 0 } }}
                      sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 } }} />
                  </Box>
                </>
              )}
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>On-Site</FieldLabel>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
                  <TextField variant="standard" size="small" type="number" placeholder="PT"
                    value={formData.personentage_onsite ?? ''}
                    onChange={(e) => handleInputChange('personentage_onsite', parseOptionalNumber(e.target.value))}
                    InputProps={{ disableUnderline: true, inputProps: { step: 0.5, min: 0 } }}
                    sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 }, flex: 1 }} />
                  <TextField variant="standard" size="small" type="number" placeholder="€"
                    value={formData.tagessatz_onsite ?? ''}
                    onChange={(e) => handleInputChange('tagessatz_onsite', parseOptionalNumber(e.target.value))}
                    InputProps={{ disableUnderline: true, inputProps: { step: 0.01, min: 0 } }}
                    sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 }, flex: 1 }} />
                </Box>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>Remote</FieldLabel>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
                  <TextField variant="standard" size="small" type="number" placeholder="PT"
                    value={formData.personentage_remote ?? ''}
                    onChange={(e) => handleInputChange('personentage_remote', parseOptionalNumber(e.target.value))}
                    InputProps={{ disableUnderline: true, inputProps: { step: 0.5, min: 0 } }}
                    sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 }, flex: 1 }} />
                  <TextField variant="standard" size="small" type="number" placeholder="€"
                    value={formData.tagessatz_remote ?? ''}
                    onChange={(e) => handleInputChange('tagessatz_remote', parseOptionalNumber(e.target.value))}
                    InputProps={{ disableUnderline: true, inputProps: { step: 0.01, min: 0 } }}
                    sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 }, flex: 1 }} />
                </Box>
              </Box>
            </>
          ) : hasPtSplit(formData) ? (
            <>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>On-Site</FieldLabel>
                <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }}>
                  {formatPt(formData.personentage_onsite)} PT
                  {formData.tagessatz_onsite != null ? ` · ${formatEur(formData.tagessatz_onsite)}` : ''}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>Remote</FieldLabel>
                <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }}>
                  {formatPt(formData.personentage_remote)} PT
                  {formData.tagessatz_remote != null ? ` · ${formatEur(formData.tagessatz_remote)}` : ''}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>{isKontingent ? 'Kontingent' : 'Gesamt'}</FieldLabel>
                <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }}>
                  {formatPt(gesamtPt)} PT{gesamtwert > 0 ? ` · ${formatEur(gesamtwert)}` : ''}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>{isKontingent ? 'Kontingent (PT)' : 'Personentage'}</FieldLabel>
                <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }}>
                  {formatPt(formData.personentage)}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>Tagessatz</FieldLabel>
                <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }}>
                  {formatEur(formData.tagessatz)}
                </Typography>
              </Box>
            </>
          ))}

          {/* Beschreibung (edit) */}
          {editing && (
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel>Beschreibung</FieldLabel>
              <TextField variant="standard" size="small" multiline maxRows={4}
                value={formData.beschreibung || ''}
                onChange={(e) => handleInputChange('beschreibung', e.target.value)}
                InputProps={{ disableUnderline: true }}
                sx={{ '& textarea': { fontSize: '0.65rem', fontWeight: 400, p: 0, lineHeight: 1.3 }, width: '100%' }} />
            </Box>
          )}
        </Box>

        {/* ── Right: Status + KPIs ──────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Paper
            elevation={0}
            sx={{
              flex: 1, p: 3, borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
              display: 'flex', flexDirection: 'column', gap: 2.5,
            }}
          >
            {/* Status + Project name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={statusName || 'Kein Status'}
                {...(editing && { onClick: (e: React.MouseEvent<HTMLElement>) => setStatusMenuAnchor(e.currentTarget) })}
                sx={{
                  fontSize: '1rem', fontWeight: 700, height: 40, px: 2,
                  bgcolor: statusColor, color: '#fff',
                  cursor: editing ? 'pointer' : 'default',
                  '& .MuiChip-label': { px: 1 },
                  ...(editing && { '&:hover': { opacity: 0.9 } }),
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {projekt?.name}
              </Typography>
            </Box>

            {/* Kontingent Progress */}
            {isKontingent && projekt?.personentage && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Kontingent: {projekt.verbrauchte_pt} / {projekt.personentage.toLocaleString('de-DE', { maximumFractionDigits: 2 })} PT ({projekt.verbrauchte_stunden}h)
                  </Typography>
                  <Typography variant="body2"
                    sx={{ fontWeight: 700, color: kontingentProgress > 90 ? '#f44336' : kontingentProgress > 70 ? '#ff9800' : '#4caf50' }}>
                    {kontingentProgress.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={kontingentProgress}
                  sx={{
                    height: 8, borderRadius: 4,
                    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      bgcolor: kontingentProgress > 90 ? '#f44336' : kontingentProgress > 70 ? '#ff9800' : '#4caf50',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">Verbraucht: {projekt.verbrauchte_pt} PT</Typography>
                  <Typography variant="caption" color="text.secondary">Verbleibend: {(projekt.personentage - projekt.verbrauchte_pt).toFixed(2)} PT</Typography>
                </Box>
              </Box>
            )}

            {/* KPI Cards */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{
                flex: '1 1 100px', px: 2, py: 1.5, borderRadius: 1.5, textAlign: 'center',
                bgcolor: isDark ? 'rgba(66,165,245,0.08)' : 'rgba(66,165,245,0.06)',
                border: `1px solid ${isDark ? 'rgba(66,165,245,0.15)' : 'rgba(66,165,245,0.12)'}`,
              }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', display: 'block' }}>
                  Audits
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#42a5f5' }}>
                  {loadingAudits ? '…' : audits.length}
                </Typography>
              </Box>

              {gesamtwert > 0 && (
                <Box sx={{
                  flex: '1 1 100px', px: 2, py: 1.5, borderRadius: 1.5, textAlign: 'center',
                  bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                }}>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', display: 'block' }}>
                    Gesamtwert
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {gesamtwert.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                  </Typography>
                </Box>
              )}

              <Box sx={{
                flex: '1 1 100px', px: 2, py: 1.5, borderRadius: 1.5, textAlign: 'center',
                bgcolor: isDark ? 'rgba(76,175,80,0.08)' : 'rgba(76,175,80,0.06)',
                border: `1px solid ${isDark ? 'rgba(76,175,80,0.15)' : 'rgba(76,175,80,0.12)'}`,
              }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', display: 'block' }}>
                  Rechnungen
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
                  {loadingRechnungen ? '…' : rechnungen.length}
                </Typography>
              </Box>

              {rechnungenSumNetto > 0 && (
                <Box sx={{
                  flex: '1 1 100px', px: 2, py: 1.5, borderRadius: 1.5, textAlign: 'center',
                  bgcolor: isDark ? 'rgba(76,175,80,0.08)' : 'rgba(76,175,80,0.06)',
                  border: `1px solid ${isDark ? 'rgba(76,175,80,0.15)' : 'rgba(76,175,80,0.12)'}`,
                }}>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', display: 'block' }}>
                    Abgerechnet
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#4caf50' }}>
                    {rechnungenSumNetto.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                  </Typography>
                </Box>
              )}

              {gesamtwert > 0 && offenerBetrag > 0 && (
                <Box sx={{
                  flex: '1 1 100px', px: 2, py: 1.5, borderRadius: 1.5, textAlign: 'center',
                  bgcolor: isDark ? 'rgba(255,152,0,0.08)' : 'rgba(255,152,0,0.06)',
                  border: `1px solid ${isDark ? 'rgba(255,152,0,0.15)' : 'rgba(255,152,0,0.12)'}`,
                }}>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', display: 'block' }}>
                    Offen
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#ff9800' }}>
                    {offenerBetrag.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ── Inline-edit menus ────────────────────────────────────────── */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={() => setStatusMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 320 } } }}>
        <MenuItem onClick={() => { handleInputChange('status_id', undefined); setStatusMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
          <em>Kein Status</em>
        </MenuItem>
        {activeFlowStatuses.map((s) => (
          <MenuItem key={s.id} selected={s.id === formData.status_id}
            onClick={() => { handleInputChange('status_id', s.id); setStatusMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
            {s.name}
          </MenuItem>
        ))}
        {(successTerminalStatuses.length > 0 || cancelledStatuses.length > 0) && (
          <MenuItem disabled sx={{ fontSize: '0.72rem', opacity: 0.7, minHeight: 28, py: 0.5 }}>
            Abschluss (direkt setzen)
          </MenuItem>
        )}
        {successTerminalStatuses.map((s) => (
          <MenuItem key={s.id} selected={s.id === formData.status_id}
            onClick={() => { handleInputChange('status_id', s.id); setStatusMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
            {s.name}
          </MenuItem>
        ))}
        {cancelledStatuses.map((s) => (
          <MenuItem key={s.id} selected={s.id === formData.status_id}
            onClick={() => { handleInputChange('status_id', s.id); setStatusMenuAnchor(null); }}
            sx={{ fontSize: '0.82rem', color: 'error.main' }}>
            {s.name}
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={typMenuAnchor} open={Boolean(typMenuAnchor)} onClose={() => setTypMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}>
        <MenuItem selected={formData.projekt_typ === 'Single'}
          onClick={() => { handleInputChange('projekt_typ', 'Single'); setTypMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
          Single (Einzelprojekt)
        </MenuItem>
        <MenuItem selected={formData.projekt_typ === 'Kontingent'}
          onClick={() => { handleInputChange('projekt_typ', 'Kontingent'); setTypMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
          Kontingent (Laufend)
        </MenuItem>
        <MenuItem selected={formData.projekt_typ === 'Festpreis'}
          onClick={() => { handleInputChange('projekt_typ', 'Festpreis'); setTypMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
          Festpreis
        </MenuItem>
      </Menu>

      <Menu anchorEl={kundeMenuAnchor} open={Boolean(kundeMenuAnchor)} onClose={() => setKundeMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}>
        <MenuItem onClick={() => { handleInputChange('kunde_id', undefined); setKundeMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
          <em>Kein Kunde</em>
        </MenuItem>
        {kunden.map((k) => (
          <MenuItem key={k.id} selected={k.id === formData.kunde_id}
            onClick={() => { handleInputChange('kunde_id', k.id); setKundeMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
            {k.name_des_unternehmens}
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={plMenuAnchor} open={Boolean(plMenuAnchor)} onClose={() => setPlMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}>
        <MenuItem onClick={() => { handleInputChange('projektleiter_id', undefined); setPlMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
          <em>Kein Projektleiter</em>
        </MenuItem>
        {mitarbeiter.map((m) => (
          <MenuItem key={m.id} selected={m.id === formData.projektleiter_id}
            onClick={() => { handleInputChange('projektleiter_id', m.id); setPlMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
            {m.vorname} {m.nachname}
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={teamMenuAnchor} open={Boolean(teamMenuAnchor)} onClose={() => setTeamMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}>
        {mitarbeiter.filter((m) => !(formData.mitarbeiter_ids || []).includes(m.id)).map((m) => (
          <MenuItem key={m.id}
            onClick={() => { handleInputChange('mitarbeiter_ids', [...(formData.mitarbeiter_ids || []), m.id]); setTeamMenuAnchor(null); }}
            sx={{ fontSize: '0.82rem' }}>
            {m.vorname} {m.nachname}
          </MenuItem>
        ))}
        {mitarbeiter.filter((m) => !(formData.mitarbeiter_ids || []).includes(m.id)).length === 0 && (
          <MenuItem disabled sx={{ fontSize: '0.82rem' }}>Alle Mitarbeiter ausgewählt</MenuItem>
        )}
      </Menu>

      {/* ══ Accordion: Audits ════════════════════════════════════════ */}
      <Accordion expanded={expandedPanels.audits} onChange={handleAccordionChange('audits')} sx={accordionSx(isDark)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
          <SecurityIcon sx={{ fontSize: 20, color: '#42a5f5' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Audits</Typography>
          {!loadingAudits && audits.length > 0 && (
            <Chip label={audits.length} size="small"
              sx={{ ml: 1, fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: isDark ? 'rgba(66,165,245,0.15)' : 'rgba(66,165,245,0.1)', color: '#42a5f5' }} />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" size="small" startIcon={<AddIcon />}
              onClick={() => navigate(`/audits/neu?projekt_id=${id}`)}>
              Neues Audit
            </Button>
          </Box>
          {loadingAudits ? (
            <Box sx={{ py: 2 }}>
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />)}
            </Box>
          ) : audits.length === 0 ? (
            <Alert severity="info">Keine Audits für dieses Projekt gefunden.</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Typ</TableCell>
                    <TableCell>Status / Fortschritt</TableCell>
                    <TableCell>Erstellt am</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audits.map((audit) => (
                    <TableRow key={audit.id} hover sx={{ cursor: 'pointer' }} {...navigableProps(navigate, `/audits/${audit.id}`)}>
                      <TableCell>{audit.name}</TableCell>
                      <TableCell>
                        {audit.audit_types?.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {audit.audit_types.map((t) =>
                              t.kuerzel ? (
                                <Box key={t.id} component="span" title={t.name}
                                  sx={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    px: 1, borderRadius: 1, fontSize: '0.75rem', fontWeight: 700,
                                    color: '#fff', backgroundColor: t.farbe || '#757575',
                                    height: 24, letterSpacing: '0.03em', whiteSpace: 'nowrap',
                                  }}>
                                  {t.kuerzel}
                                </Box>
                              ) : (
                                <span key={t.id}>{t.name}</span>
                              ),
                            )}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 360 }}>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}>
                            {audit.audit_status?.name || 'Kein Status'}
                          </Typography>
                          {auditStatuses.length > 0 ? (
                            <AuditProgressPipeline statuses={auditStatuses} currentStatusId={audit.audit_status?.id} compact />
                          ) : (
                            <Chip label={audit.audit_status?.name || 'Kein Status'} size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{format(new Date(audit.erstellt_am), 'dd.MM.yyyy')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ══ Accordion: Rechnungen ════════════════════════════════════ */}
      <Accordion expanded={expandedPanels.rechnungen} onChange={handleAccordionChange('rechnungen')} sx={accordionSx(isDark)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
          <ReceiptIcon sx={{ fontSize: 20, color: '#4caf50' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Rechnungen</Typography>
          {!loadingRechnungen && rechnungen.length > 0 && (
            <Chip label={rechnungen.length} size="small"
              sx={{ ml: 1, fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: isDark ? 'rgba(76,175,80,0.15)' : 'rgba(76,175,80,0.1)', color: '#4caf50' }} />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {rechnungen.length > 0 && `Gesamt: ${rechnungenSumNetto.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € netto`}
            </Typography>
            <Button variant="outlined" size="small" startIcon={<ReceiptIcon />}
              onClick={() => navigate(`/rechnungen/neu?projekt_id=${id}`)}>
              Manuelle Rechnung
            </Button>
          </Box>

          {/* Offene Monate (Kontingent) */}
          {isKontingent && offeneMonate.length > 0 && (
            <Card sx={{ mb: 2 }} elevation={0}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', fontSize: '0.65rem' }}>
                  Offene Monate (nicht abgerechnet)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {offeneMonate.map((om) => {
                    const [y, mo] = om.monat.split('-');
                    const monatName = new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
                    const pt = (om.stunden / (projekt?.stunden_pro_pt || 8)).toFixed(2);
                    return (
                      <Chip key={om.monat}
                        label={`${monatName}: ${om.stunden.toFixed(1)}h (${pt} PT) – Abrechnen`}
                        color="warning" variant="outlined"
                        onClick={() => setSchnellabrechnungMonat(om)}
                        sx={{ cursor: 'pointer', fontWeight: 500 }} />
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          )}

          {isKontingent && offeneMonate.length === 0 && !loadingRechnungen && (
            <Alert severity="success" sx={{ mb: 2 }}>Alle Zeiteinträge sind abgerechnet.</Alert>
          )}

          {loadingRechnungen ? (
            <Box sx={{ py: 2 }}>
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />)}
            </Box>
          ) : rechnungen.length === 0 ? (
            <Alert severity="info">Keine Rechnungen für dieses Projekt gefunden.</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nr.</TableCell>
                    <TableCell>Datum</TableCell>
                    <TableCell>Leistungszeitraum</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Netto</TableCell>
                    <TableCell align="right">Brutto</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rechnungen.map((re) => (
                    <TableRow key={re.id} hover sx={{ cursor: 'pointer' }} {...navigableProps(navigate, `/rechnungen/${re.id}`)}>
                      <TableCell>{re.rechnungsnummer}</TableCell>
                      <TableCell>{format(new Date(re.rechnungsdatum), 'dd.MM.yyyy')}</TableCell>
                      <TableCell>
                        {format(new Date(re.leistungszeitraum_von), 'dd.MM.yyyy')} – {format(new Date(re.leistungszeitraum_bis), 'dd.MM.yyyy')}
                      </TableCell>
                      <TableCell>
                        <Chip label={re.status} size="small" color={getInvoiceStatusColor(re.status)} />
                      </TableCell>
                      <TableCell align="right">{re.nettobetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</TableCell>
                      <TableCell align="right">{re.bruttobetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</TableCell>
                      <TableCell align="right">
                        <Tooltip title="PDF herunterladen">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); rechnungenService.generatePdf(re.id); }}>
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ══ Accordion: Dokumente ═════════════════════════════════════ */}
      {!isNew && id && (
        <Accordion expanded={expandedPanels.dokumente} onChange={handleAccordionChange('dokumente')} sx={accordionSx(isDark)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
            <FolderOpenIcon sx={{ fontSize: 20, color: '#ab47bc' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Dokumente</Typography>
            {dokumentCount > 0 && (
              <Chip
                label={dokumentCount}
                size="small"
                sx={{
                  ml: 1,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                  bgcolor: isDark ? 'rgba(171,71,188,0.15)' : 'rgba(171,71,188,0.1)',
                  color: '#ab47bc',
                }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, py: 2 }}>
            <DokumentenAblage
              projektId={parseInt(id, 10)}
              onFileCountChange={setDokumentCount}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* ══ Accordion: Zeiterfassung (nur Kontingent) ════════════════ */}
      {isKontingent && (
        <Accordion expanded={expandedPanels.zeiterfassung} onChange={handleAccordionChange('zeiterfassung')} sx={accordionSx(isDark)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
            <AccessTimeIcon sx={{ fontSize: 20, color: '#ff9800' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zeiterfassung</Typography>
            {zeiteintraege.length > 0 && (
              <Chip
                label={`${zeiteintraege.reduce((s, ze) => s + ze.stunden, 0).toFixed(1)}h`}
                size="small"
                sx={{ ml: 1, fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: isDark ? 'rgba(255,152,0,0.15)' : 'rgba(255,152,0,0.1)', color: '#ff9800' }} />
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Zeiteinträge</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {getMonths().map((monat) => (
                  <Button key={monat} variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
                    onClick={() => setZeitnachweisDialogMonat(monat)}>
                    Zeitnachweis {monat}
                  </Button>
                ))}
                <Button variant="contained" size="small" startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingZeiteintragId(null);
                    setNeuerZeiteintragMitarbeiterIds(mitarbeiter.length > 0 ? [mitarbeiter[0].id] : []);
                    setZeiteintragForm({
                      datum: toLocalDateString(),
                      stunden: 8,
                      beschreibung: '',
                      mitarbeiter_id: mitarbeiter.length > 0 ? mitarbeiter[0].id : 0,
                      projekt_id: parseInt(id!),
                      audit_id: undefined,
                    });
                    setZeiteintragDialogOpen(true);
                  }}>
                  Eintrag hinzufügen
                </Button>
              </Box>
            </Box>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Von"
                  type="date"
                  value={zeiteintragFilterVon}
                  onChange={(e) => setZeiteintragFilterVon(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bis"
                  type="date"
                  value={zeiteintragFilterBis}
                  onChange={(e) => setZeiteintragFilterBis(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Mitarbeiter</InputLabel>
                  <Select
                    value={zeiteintragFilterMitarbeiterId}
                    onChange={(e) => setZeiteintragFilterMitarbeiterId(e.target.value as number | '')}
                    label="Mitarbeiter"
                  >
                    <MenuItem value=""><em>Alle</em></MenuItem>
                    {mitarbeiter.map((m) => (
                      <MenuItem key={m.id} value={m.id}>{m.vorname} {m.nachname}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Audit</InputLabel>
                  <Select
                    value={zeiteintragFilterAuditId}
                    onChange={(e) => setZeiteintragFilterAuditId(e.target.value as number | '')}
                    label="Audit"
                  >
                    <MenuItem value=""><em>Alle</em></MenuItem>
                    {audits.map((a) => (
                      <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Abgerechnet</InputLabel>
                  <Select
                    value={zeiteintragFilterAbgerechnet}
                    onChange={(e) => setZeiteintragFilterAbgerechnet(e.target.value as AbgerechnetFilter)}
                    label="Abgerechnet"
                  >
                    <MenuItem value="alle">Alle</MenuItem>
                    <MenuItem value="abgerechnet">Nur abgerechnet</MenuItem>
                    <MenuItem value="nicht_abgerechnet">Nur nicht abgerechnet</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="text" size="small" onClick={handleResetZeiteintragFilter}>
                    Filter zurücksetzen
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {loadingZeiteintraege ? (
              <Box sx={{ py: 2 }}>
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />)}
              </Box>
            ) : zeiteintraege.length === 0 ? (
              <Alert severity="info">Keine Zeiteinträge vorhanden.</Alert>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Datum</TableCell>
                      <TableCell>Mitarbeiter</TableCell>
                      <TableCell>Audit</TableCell>
                      <TableCell align="right">Stunden</TableCell>
                      <TableCell>Beschreibung</TableCell>
                      <TableCell>Abgerechnet</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {zeiteintraege.map((ze) => (
                      <TableRow key={ze.id}>
                        <TableCell>{format(new Date(ze.datum), 'dd.MM.yyyy')}</TableCell>
                        <TableCell>
                          {ze.mitarbeiter ? (
                            <MitarbeiterKurzprofilLink mitarbeiterId={ze.mitarbeiter_id}>
                              {ze.mitarbeiter.vorname} {ze.mitarbeiter.nachname}
                            </MitarbeiterKurzprofilLink>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{getAuditName(ze.audit_id)}</TableCell>
                        <TableCell align="right">{ze.stunden.toFixed(1)}h</TableCell>
                        <TableCell>{ze.beschreibung || '-'}</TableCell>
                        <TableCell>
                          {ze.rechnung_id
                            ? <Chip label="Ja" size="small" color="success" />
                            : <Chip label="Nein" size="small" color="default" />}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                            {ze.rechnung_id && canUnlinkAbrechnung && (
                              <Tooltip title="Abrechnungsmarkierung aufheben">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setUnlinkTarget(ze);
                                    setUnlinkConfirmOpen(true);
                                  }}
                                >
                                  <LinkOffIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!ze.rechnung_id && (
                              <>
                              {canEditZeiteintraege && (
                                <Tooltip title="Bearbeiten">
                                  <IconButton size="small" color="primary" onClick={() => openEditZeiteintragDialog(ze)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Löschen">
                                <IconButton size="small" color="error" onClick={() => handleDeleteZeiteintrag(ze.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {zeiteintraege.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Gesamt: {zeiteintraege.reduce((s, ze) => s + ze.stunden, 0).toFixed(1)}h
                  = {(zeiteintraege.reduce((s, ze) => s + ze.stunden, 0) / (projekt?.stunden_pro_pt || 8)).toFixed(2)} PT
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nicht abgerechnet: {zeiteintraege.filter((ze) => !ze.rechnung_id).reduce((s, ze) => s + ze.stunden, 0).toFixed(1)}h
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* ══ Accordion: Beschreibung ══════════════════════════════════ */}
      {formData.beschreibung && !editing && (
        <Accordion expanded={expandedPanels.beschreibung} onChange={handleAccordionChange('beschreibung')} sx={accordionSx(isDark)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
            <DescriptionOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Beschreibung</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {formData.beschreibung}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Zeiteintrag Dialog ───────────────────────────────────────── */}
      {/* Dialog: Template-Auswahl für Leistungsnachweis */}
      <Dialog open={zeitnachweisDialogMonat !== null} onClose={() => setZeitnachweisDialogMonat(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Leistungsnachweis – Vorlage wählen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Für welche Vorlage soll der Leistungsnachweis{zeitnachweisDialogMonat ? ` (${zeitnachweisDialogMonat})` : ''} generiert werden?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.2 }}
              onClick={() => {
                if (zeitnachweisDialogMonat) {
                  zeiteintraegeService.downloadZeitnachweis(parseInt(id!), zeitnachweisDialogMonat, 'enbw');
                }
                setZeitnachweisDialogMonat(null);
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>EnBW-Vorlage</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Projektspezifisches SOLCOM-Layout (Standard)</Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.2 }}
              onClick={() => {
                if (zeitnachweisDialogMonat) {
                  zeiteintraegeService.downloadZeitnachweis(parseInt(id!), zeitnachweisDialogMonat, 'stark');
                }
                setZeitnachweisDialogMonat(null);
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>STARK-Vorlage</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Neutrales STARK-Design (weiß, grüne Akzente)</Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZeitnachweisDialogMonat(null)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={zeiteintragDialogOpen} onClose={() => { setZeiteintragDialogOpen(false); setEditingZeiteintragId(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingZeiteintragId ? 'Zeiteintrag bearbeiten' : 'Neuer Zeiteintrag'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Datum" type="date" value={zeiteintragForm.datum}
                onChange={(e) => setZeiteintragForm((p) => ({ ...p, datum: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Stunden" type="number" value={zeiteintragForm.stunden}
                onChange={(e) => setZeiteintragForm((p) => ({ ...p, stunden: parseFloat(e.target.value) || 0 }))}
                InputProps={{ inputProps: { step: 0.5, min: 0.5, max: 24 } }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Mitarbeiter</InputLabel>
                {editingZeiteintragId ? (
                  <Select
                    value={zeiteintragForm.mitarbeiter_id || ''}
                    onChange={(e) => setZeiteintragForm((p) => ({ ...p, mitarbeiter_id: e.target.value as number }))}
                    label="Mitarbeiter"
                  >
                    {mitarbeiter.map((m) => (
                      <MenuItem key={m.id} value={m.id}>{m.vorname} {m.nachname}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Select
                    multiple
                    value={neuerZeiteintragMitarbeiterIds}
                    onChange={(e) => setNeuerZeiteintragMitarbeiterIds(e.target.value as number[])}
                    label="Mitarbeiter"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((vid) => {
                          const m = mitarbeiter.find((ma) => ma.id === vid);
                          return <Chip key={vid} label={m ? `${m.vorname} ${m.nachname}` : vid} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {mitarbeiter.map((m) => (
                      <MenuItem key={m.id} value={m.id}>{m.vorname} {m.nachname}</MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Audit (optional)</InputLabel>
                <Select value={zeiteintragForm.audit_id || ''}
                  onChange={(e) => setZeiteintragForm((p) => ({ ...p, audit_id: (e.target.value as number) || undefined }))}
                  label="Audit (optional)">
                  <MenuItem value=""><em>Kein Audit</em></MenuItem>
                  {audits.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Beschreibung" value={zeiteintragForm.beschreibung}
                onChange={(e) => setZeiteintragForm((p) => ({ ...p, beschreibung: e.target.value }))}
                multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setZeiteintragDialogOpen(false); setEditingZeiteintragId(null); }}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSaveZeiteintrag}
            disabled={
              editingZeiteintragId
                ? !zeiteintragForm.mitarbeiter_id
                : neuerZeiteintragMitarbeiterIds.length === 0
            }
          >
            {editingZeiteintragId ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Schnellabrechnung Bestätigungsdialog ─────────────────────── */}
      <Dialog open={!!schnellabrechnungMonat} onClose={() => setSchnellabrechnungMonat(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Monat abrechnen</DialogTitle>
        <DialogContent>
          {schnellabrechnungMonat && (() => {
            const [y, mo] = schnellabrechnungMonat.monat.split('-');
            const monatName = new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
            const stundenProPt = projekt?.stunden_pro_pt || 8;
            const pt = (schnellabrechnungMonat.stunden / stundenProPt).toFixed(2);
            const tagessatz = projekt?.tagessatz || 0;
            const netto = (parseFloat(pt) * tagessatz).toFixed(2);
            return (
              <Box>
                <Typography sx={{ mb: 2 }}>
                  Möchten Sie den Monat <strong>{monatName}</strong> abrechnen?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {schnellabrechnungMonat.anzahl} Zeiteinträge, {schnellabrechnungMonat.stunden.toFixed(1)}h = {pt} PT
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nettobetrag: {parseFloat(netto).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                  ({tagessatz.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €/PT)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Es wird eine Rechnung im Status &quot;Entwurf&quot; erstellt.
                  Nur Zeiteinträge dieses Kalendermonats werden als abgerechnet markiert – nicht der Zeitraum bis zum Rechnungsdatum.
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSchnellabrechnungMonat(null)} sx={{ color: 'text.secondary' }}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSchnellabrechnung} disabled={schnellabrechnungLoading}
            startIcon={schnellabrechnungLoading ? <CircularProgress size={16} /> : <ReceiptIcon />}>
            Rechnung erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ───────────────────────────────────────────── */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Projekt löschen"
        message={
          filesToDelete.length > 0
            ? 'Möchten Sie dieses Projekt wirklich löschen? Dabei werden folgende Dateien unwiderruflich mitgelöscht. Diese Aktion kann nicht rückgängig gemacht werden.'
            : 'Möchten Sie dieses Projekt wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
        }
        onConfirm={handleDelete}
        onCancel={() => { setDeleteDialogOpen(false); setFilesToDelete([]); }}
        additionalContent={
          filesToDelete.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Mitgelöschte Dateien ({filesToDelete.length}):
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  maxHeight: 120,
                  overflow: 'auto',
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                {filesToDelete.map((f) => (
                  <li key={f.id}>{f.dateiname}</li>
                ))}
              </Box>
            </Box>
          ) : undefined
        }
      />

      <ConfirmDialog
        open={unlinkConfirmOpen}
        title="Abrechnungsmarkierung aufheben"
        message={
          unlinkTarget
            ? `Die Markierung „abgerechnet“ für den Eintrag vom ${format(new Date(unlinkTarget.datum), 'dd.MM.yyyy')} (${unlinkTarget.stunden}h) wird aufgehoben. Die Rechnung selbst bleibt unverändert.`
            : ''
        }
        onConfirm={handleUnlinkAbrechnung}
        onCancel={() => {
          setUnlinkConfirmOpen(false);
          setUnlinkTarget(null);
        }}
        confirmText="Aufheben"
      />

      {/* ── Historie ─────────────────────────────────────────────────── */}
      {id && (
        <HistorieTimeline
          entitaetTyp="Projekt"
          entitaetId={parseInt(id)}
          open={historieOpen}
          onClose={() => setHistorieOpen(false)}
        />
      )}
    </Box>
  );
};

export default ProjektDetailPage;
